import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Button, Grid, IconButton, InputAdornment, TextField } from '@mui/material'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import SearchIcon from '@mui/icons-material/Search'
import { query } from '@cbgms/components/list'
import { searchPendingLog, updateQuery } from '../state/actions'
import {
  ApiResponses,
  CustomerEmailInterface,
  CustomerPendingEmailSearchQuery,
  CustomersBySearchQueryProps,
  PendingLogSearchQuery,
  UseSearchByQuery,
  getPendingEmailTableQuery,
  getPendingEmailTableQueryByTemplateName
} from '@cbgms/api/modules/customers'
import { useSafeState } from '@cbgms/base/hooks/use-safe-state'
import { MenuItem } from 'core/page/MenuItem'
import { useNotifier } from '@cbgms/base/notifier'
import { notifier } from '@cbgms/base/notifier/use-notifier'
import store from '../state/store'

export const filterPendingLogsByTemplateName = (
  pendingLogs: CustomerEmailInterface[],
  selectedTemplateName: string
): CustomerEmailInterface[] => {
  const normalizedTemplateName = selectedTemplateName.trim().toLowerCase()

  if (!normalizedTemplateName) {
    return pendingLogs
  }

  return pendingLogs.filter(({ TemplateName }) => TemplateName.trim().toLowerCase() === normalizedTemplateName)
}

export const SearchByQuery: React.FC<CustomersBySearchQueryProps> = ({ setAPISearchQuery }) => {
  const [queryInputValue, setqueryInputValue] = useSafeState<string>('')
  const dispatch = useDispatch()

  const { t } = useTranslation()
  const notifier = useNotifier()

  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      const state = store.getState()
      setqueryInputValue(state.Query)
    })

    return () => {
      unsubscribe()
    }
  }, [])

  const isMounted = React.useRef(false)
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true
      return // skip on initial mount - avoid an unnecessary search-by-blank-query on every page load
    }
    if (queryInputValue === '') {
      updateSearchQuery('')
    }
  }, [queryInputValue])

  const getCustomersBySearchQuery = React.useCallback(async (): Promise<string> => {
    const customerSearchQueryInList = query<CustomerPendingEmailSearchQuery>() as PendingLogSearchQuery

    const response: ApiResponses<CustomerEmailInterface[]> = await dispatch(searchPendingLog(store.getState()))
    const customersFoundByQuery = response.Data
    return getPendingEmailTableQuery(customersFoundByQuery, customerSearchQueryInList)
  }, [])

  const updateSearchQuery = React.useCallback(
    async (QueryString: string) => {
      try {
        store.dispatch(updateQuery(QueryString))
        const customerSearchQueryInList = await getCustomersBySearchQuery()

        setAPISearchQuery(customerSearchQueryInList)
      } catch (e) {
        if (e instanceof Error) {
          notifier.error(e.message)
        } else {
          notifier.error('An unknown error occurred')
        }
      }
    },
    [setAPISearchQuery]
  )

  const queryStringOnChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const searchQuery = event.target.value.slice(0, 255)
    setqueryInputValue(searchQuery)
    if (!searchQuery) {
      updateSearchQuery('')
    }
  }, [])

  const queryStringOnKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (queryInputValue.length >= 3) {
        updateSearchQuery(queryInputValue)
      }
    }
  }

  return (
    <Grid item display='flex' alignItems='center' gap={1} data-testid='search-component'>
      <TextField
        size='small'
        sx={{ minWidth: 450 }}
        variant='outlined'
        placeholder={t('grid.search_by_cus_eos_id_email_to_error_details')}
        type='text'
        onChange={queryStringOnChange}
        onKeyDown={queryStringOnKeyDown}
        value={queryInputValue}
        data-testid='search-component-input'
        InputProps={{
          endAdornment: (
            <InputAdornment position='end'>
              {queryInputValue && (
                <IconButton
                  onClick={() => {
                    setqueryInputValue('')
                    updateSearchQuery('')
                  }}
                  size='large'
                >
                  <HighlightOffIcon />
                </IconButton>
              )}
            </InputAdornment>
          )
        }}
      />
      <Button
        startIcon={<SearchIcon />}
        variant='contained'
        disableElevation
        onClick={() => updateSearchQuery(queryInputValue)}
        disabled={queryInputValue.length < 3}
        data-testid='search-component-button'
      >
        {t('global.search')}
      </Button>
    </Grid>
  )
}

export const useSearchCustomers = (): UseSearchByQuery => {
  const [apiSearchQuery, setAPISearchQuery] = useSafeState<string>('')
  const dispatch = useDispatch()

  async function apiDropDownFilters() {
    const { Query, TemplateName } = store.getState()

    // No search text: filter directly by template, no need to round-trip through the search endpoint
    if (!Query) {
      return TemplateName ? `PendingLogsOutput.TemplateName|=|${TemplateName}` : ''
    }

    const customerSearchQueryInList = query<CustomerPendingEmailSearchQuery>() as PendingLogSearchQuery
    const response: ApiResponses<CustomerEmailInterface[]> = await dispatch(searchPendingLog(store.getState()))
    const customersFoundByQuery = filterPendingLogsByTemplateName(response.Data, TemplateName)

    return getPendingEmailTableQueryByTemplateName(customersFoundByQuery, customerSearchQueryInList)
  }

  async function updateListBYDropDownFilters() {
    try {
      const customerSearchQueryInList = await apiDropDownFilters()
      setAPISearchQuery(customerSearchQueryInList)
    } catch (e: unknown) {
      if (e instanceof Error) {
        notifier.error(e.message)
      } else {
        notifier.error('An unknown error occurred')
      }
    }
  }

  return {
    getSearchComponent: () => (
      <MenuItem>
        <SearchByQuery setAPISearchQuery={setAPISearchQuery} />
      </MenuItem>
    ),
    apiSearchQuery,
    updateListBYDropDownFilters
  }
}
